import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../shared/ui";
import { motionOn, onReveal, prefersReducedMotion } from "../../shared/motion";
import { STACK, loc, type Lang, type StackItem } from "./mock";

// Prototype timings (landing-v4.js): first swipe after 3.2 s, then every 4.4 s; 1.6 s after the
// hero scrolls back into view or the tab becomes visible again.
const T = { first: 3200, press: 110, fly: 620, cycle: 4400, resume: 1600, remove: 420 };

type Phase = "rest" | "enter" | "pressing" | "stamped" | "fly";
interface Card {
  key: number;
  idx: number;
  phase: Phase;
}

const INITIAL: Card[] = [0, 1, 2].map((i) => ({ key: i, idx: i, phase: "rest" }));

const FLY: CSSProperties = { transform: "translateX(420px) rotate(19deg)", opacity: 0, zIndex: 4 };
const ENTER: CSSProperties = { transform: "translateY(33px) scale(0.865)", opacity: 0, zIndex: 0 };
const restStyle = (k: number): CSSProperties => ({
  transform: `translateY(${k * 11}px) scale(${+(1 - k * 0.045).toFixed(3)})`,
  opacity: 1,
  zIndex: 3 - k,
});

interface PendingStackProps {
  /** The cycle only runs while this element (the hero) is on screen. */
  hostRef: RefObject<HTMLElement>;
  /** Called when the top card flies off — the hero shows the matching toast. */
  onToast: (it: StackItem) => void;
}

// The "pending confirmations" card stack (prototype 46–94): the top card's button is pressed,
// stamped, swiped off to the right and a new card slides in at the back. The prerendered markup
// is the resting 3-card stack; nothing moves under reduced motion or without the anim class.
export function PendingStack({ hostRef, onToast }: PendingStackProps) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";
  const [cards, setCards] = useState(INITIAL);
  const cardsRef = useRef(INITIAL);
  const keyRef = useRef(INITIAL.length);
  const onToastRef = useRef(onToast);
  useEffect(() => {
    onToastRef.current = onToast;
  });

  useEffect(() => {
    if (prefersReducedMotion() || !motionOn()) return;
    const timers = new Set<number>();
    const rafs = new Set<number>();
    const later = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const nextFrame = (fn: () => void) => {
      const id = requestAnimationFrame(() => {
        rafs.delete(id);
        fn();
      });
      rafs.add(id);
    };
    const commit = (next: Card[]) => {
      cardsRef.current = next;
      setCards(next);
    };
    const patch = (key: number, phase: Phase) =>
      commit(cardsRef.current.map((c) => (c.key === key ? { ...c, phase } : c)));

    const step = () => {
      const top = cardsRef.current.find((c) => c.phase !== "fly");
      if (!top) return;
      patch(top.key, "pressing");
      later(() => patch(top.key, "stamped"), T.press);
      later(() => {
        const live = cardsRef.current.filter((c) => c.phase !== "fly");
        const last = live[live.length - 1];
        const entering: Card = { key: keyRef.current++, idx: (last.idx + 1) % STACK.length, phase: "enter" };
        // The new card must be in the DOM at its start position before it moves to the back slot,
        // otherwise there is no transition to run.
        flushSync(() =>
          commit([...cardsRef.current.map((c) => (c.key === top.key ? { ...c, phase: "fly" as const } : c)), entering]),
        );
        onToastRef.current(STACK[top.idx]);
        nextFrame(() => nextFrame(() => patch(entering.key, "rest")));
        later(() => commit(cardsRef.current.filter((c) => c.key !== top.key)), T.remove);
      }, T.fly);
    };

    let cycle = 0;
    let heroOn = true;
    let started = false;
    const tick = () => {
      step();
      cycle = window.setTimeout(tick, T.cycle);
    };
    const run = () => {
      clearTimeout(cycle);
      cycle = 0;
      if (!heroOn || document.hidden) return;
      cycle = window.setTimeout(tick, started ? T.resume : T.first);
      started = true;
    };

    let io: IntersectionObserver | undefined;
    const offReveal = onReveal(() => {
      const host = hostRef.current;
      if (!host) return;
      io = new IntersectionObserver((es) => {
        heroOn = es[0].isIntersecting;
        run();
      });
      io.observe(host);
      document.addEventListener("visibilitychange", run);
    });

    return () => {
      offReveal();
      io?.disconnect();
      document.removeEventListener("visibilitychange", run);
      clearTimeout(cycle);
      timers.forEach((id) => clearTimeout(id));
      rafs.forEach((id) => cancelAnimationFrame(id));
    };
  }, [hostRef]);

  let k = 0;
  return (
    <div className="stack">
      {cards.map((c) => {
        const it = STACK[c.idx];
        const style = c.phase === "fly" ? FLY : c.phase === "enter" ? ENTER : restStyle(k++);
        const acc = it.kind === "in";
        const pressing = c.phase === "pressing" ? " pressing" : "";
        const stamped = c.phase === "stamped" || c.phase === "fly";
        const time = it.time === "yesterday" ? t("hero.stack.yesterday") : loc(it.time, lang);
        const status = acc
          ? t("hero.stack.payment_in")
          : t(it.kind === "over" ? "hero.stack.days_late" : "hero.stack.days_until", { n: it.days });
        return (
          <div key={c.key} className="pcw" style={style}>
            <div className="pc">
              <div className="pc-top">
                <span className={"pc-av" + (it.kind === "over" ? " t-over" : it.kind === "up" ? " t-up" : "")}>
                  {it.ini}
                  <i>
                    <Icon name={acc ? "i-arrowDown" : "i-clock"} />
                  </i>
                </span>
                <div className="grow">
                  <div className="pc-nm">{it.name}</div>
                  <div className="pc-pr">
                    {loc(it.product, lang)}
                    {it.months && ` · ${t("hero.mock.months", { a: it.months[0], b: it.months[1] })}`}
                  </div>
                </div>
                <span className="pc-tm">{time}</span>
              </div>
              <div className="pc-amt">
                <b>{loc(it.amount, lang)}</b>
                <span className={it.kind === "over" ? "red" : undefined}>{status}</span>
              </div>
              <div className="pc-btns">
                {acc ? (
                  <>
                    <span className="pcb rej">{t("hero.stack.reject")}</span>
                    <span className={"pcb acc" + pressing}>
                      <Icon name="i-check" />
                      {t("hero.stack.accept")}
                    </span>
                  </>
                ) : (
                  <span className={"pcb acc one" + pressing}>
                    <Icon name="i-bell" />
                    {t("hero.stack.remind")}
                  </span>
                )}
              </div>
              {stamped && <span className="stamp">{t(acc ? "hero.stack.stamp_accept" : "hero.stack.stamp_remind")}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
